if (!customElements.get('cart-note')) {
  class CartNote extends HTMLElement {
    constructor() {
      super();
      this.handleInput = this.handleInput.bind(this);
      this.handleSaveClick = this.handleSaveClick.bind(this);
      this.addEventListener('input', this.handleInput);
      this.addEventListener('click', this.handleSaveClick);
      this.initialize();
    }

    connectedCallback() {
      this.addEventListener('input', this.handleInput);
      this.addEventListener('click', this.handleSaveClick);
      if (!this.input) this.initialize();
    }

    disconnectedCallback() {
      clearTimeout(this.noteTimer);
      this.removeEventListener('input', this.handleInput);
      this.removeEventListener('click', this.handleSaveClick);
    }

    initialize() {
      this.input = this.querySelector('textarea');
      this.confirmedNote = this.input?.value || '';
      this.editVersion = CartNote.latestEdit;
    }

    handleInput() {
      this.input = this.input || this.querySelector('textarea');
      this.editVersion = ++CartNote.latestEdit;
      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(() => this.save(), 300);
    }

    async handleSaveClick(evt) {
      const button = evt.target.closest('[data-note-save]');
      if (!button || !this.contains(button) || button.disabled) return;
      evt.preventDefault();
      button.disabled = true;
      button.classList.add('is-loading');
      try {
        if (!await this.save()) this.input?.focus();
      } finally {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }

    setState(state) {
      const status = this.querySelector('[data-note-status]');
      const error = this.querySelector('[data-note-error]');
      if (state === 'saving') this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
      if (status) status.textContent = state === 'saving' ? this.dataset.savingLabel : (state === 'saved' ? this.dataset.savedLabel : '');
      if (error) {
        error.hidden = state !== 'error';
        if (state === 'error') error.textContent = this.dataset.errorLabel || theme.strings.cartError;
      }
    }

    save() {
      clearTimeout(this.noteTimer);
      this.input = this.input || this.querySelector('textarea');
      if (!this.input) return Promise.resolve(true);
      if (this.savePromise) return this.savePromise;
      if (this.input.value === this.confirmedNote) return Promise.resolve(true);
      this.setState('saving');
      this.savePromise = Promise.resolve().then(async () => {
        try {
          while (this.input.value !== this.confirmedNote) {
            const value = this.input.value;
            const version = this.editVersion;
            const write = CartNote.writeQueue.then(async () => {
              if (version < CartNote.latestEdit) return false;
              const response = await fetch(theme.routes.cartUpdate, {
                method: 'POST',
                headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify({ note: value })
              });
              if (!response.ok) throw new Error('Note update unavailable');
              const cart = await response.json();
              if (String(cart.note || '') !== value) throw new Error('Note update unconfirmed');
              return true;
            });
            CartNote.writeQueue = write.catch(() => {});
            const confirmed = await write;
            if (!confirmed) {
              if (version !== this.editVersion) continue;
              throw new Error('Note edit superseded');
            }
            this.confirmedNote = value;
          }
          const toggle = this.closest('details')?.querySelector('.js-show-note');
          if (toggle) toggle.textContent = this.confirmedNote ? theme.strings.editCartNote : theme.strings.addCartNote;
          this.setState('saved');
          return true;
        } catch (error) {
          this.setState('error');
          return false;
        }
      }).finally(() => { this.savePromise = null; });
      return this.savePromise;
    }
  }

  CartNote.writeQueue = Promise.resolve();
  CartNote.latestEdit = 0;
  customElements.define('cart-note', CartNote);
}
